/**
 * Service-area pages.
 *
 * These are deliberately NOT templated "we do SEO in [CITY]" pages. Near-
 * duplicate location pages generated from a template are doorway pages, and
 * Google's guidelines name them explicitly as a spam pattern. Each entry here
 * carries market detail specific to that city, and the page renders unique
 * body copy rather than string-substituting a city name into shared boilerplate.
 *
 * Equally important: WeBuildPro Global is a remote-first agency and has no
 * office in these cities. No page here emits LocalBusiness schema or a
 * physical address it does not have — that is fabricated local data and
 * carries exactly the manual-action risk that fake reviews do. These are
 * `Service` pages with an honest `areaServed`, and the copy says plainly that
 * the agency works remotely.
 *
 * Add a city here only when there is something genuinely specific to say
 * about that market. A page with nothing particular to say should not exist.
 */

export interface LocationFAQ {
  question: string;
  answer: string;
}

export interface LocationDefinition {
  slug: string;
  /** City name as locals write it. */
  city: string;
  region: string;
  regionCode: string;
  country: string;
  metaTitle: string;
  metaDescription: string;
  h1: string;
  /** 40-60 word extractable opener. */
  directAnswer: string;
  cardSummary: string;
  keywords: string[];
  /** Genuinely market-specific observations. Not interchangeable. */
  marketContext: string[];
  /** What tends to work in this specific market, and why. */
  whatWorksHere: { heading: string; body: string }[];
  /** Industries that are actually significant in this market. */
  commonIndustries: string[];
  faqs: LocationFAQ[];
  /** Service slugs most relevant to this market. */
  priorityServices: string[];
}

export const LOCATIONS: LocationDefinition[] = [
  {
    slug: 'toronto',
    city: 'Toronto',
    region: 'Ontario',
    regionCode: 'ON',
    country: 'Canada',
    metaTitle: 'Toronto Digital Marketing Agency',
    metaDescription:
      'Websites, SEO and Google Ads management for Toronto service businesses — built for one of the most expensive and competitive ad markets in Canada.',
    h1: 'Digital Marketing for Toronto Service Businesses',
    directAnswer:
      'Toronto is among the most expensive paid search markets in Canada, with home-service cost per click frequently running well above the national average. WeBuildPro Global builds websites, search visibility and Google Ads campaigns for Toronto service businesses, with the keyword discipline that high click costs make non-negotiable.',
    cardSummary:
      'Canada’s most competitive service market — where wasted clicks cost the most.',
    keywords: [
      'digital marketing agency toronto',
      'google ads management toronto',
      'seo agency toronto',
      'web design toronto service business',
      'ppc agency toronto',
      'marketing for toronto contractors',
    ],
    marketContext: [
      'Toronto has both the highest concentration of service businesses in Canada and the highest competition for their customers. Every established trade in the city is already bidding on the obvious keywords, and many have been doing so for years with budgets a newer business cannot match head-on.',
      'That competition shows up directly in cost per click. In home-service categories, Toronto clicks routinely cost several times what the same keyword costs in a smaller Canadian market. A campaign structured loosely here burns a monthly budget in days rather than weeks.',
      'The city is also unusually fragmented geographically. "Toronto" to a customer in Scarborough and "Toronto" to a customer in Etobicoke describe meaningfully different service calls, and a campaign that treats the GTA as one undifferentiated radius pays city-centre prices for leads it cannot profitably serve.',
    ],
    whatWorksHere: [
      {
        heading: 'Neighbourhood-level targeting beats city-wide radius',
        body: 'Rather than bidding across the whole GTA, campaigns are segmented by the areas a business can actually reach profitably. A trade based in North York bidding equally on downtown and Mississauga searches pays premium rates for jobs that may not be worth the travel. Tightening geography is usually the fastest reduction in wasted spend available in a Toronto account.',
      },
      {
        heading: 'Long-tail keywords where head terms are unaffordable',
        body: 'Competing on a two-word head term in Toronto means outbidding established businesses with years of conversion data feeding their bid strategies. Specific, longer searches — the problem plus the neighbourhood, or the problem plus its urgency — carry far lower competition and considerably higher intent.',
      },
      {
        heading: 'Conversion rate matters more when clicks cost more',
        body: 'At high cost per click, a landing page converting at two percent instead of four effectively doubles cost per lead. In cheaper markets a mediocre page is survivable; in Toronto it is the difference between a channel that works and one that does not. Conversion work usually deserves attention before any budget increase here.',
      },
    ],
    commonIndustries: [
      'Plumbing, drain and HVAC services',
      'Electrical and general contracting',
      'Roofing and exterior work',
      'Cleaning and property maintenance',
      'Professional services — legal, accounting, consulting',
      'Health, dental and wellness practices',
    ],
    faqs: [
      {
        question: 'How much do Google Ads cost in Toronto?',
        answer:
          'Toronto sits at the expensive end of the Canadian market. In competitive home-service categories, cost per click commonly runs well into double digits, which means a small budget buys few clicks. Your strategy call includes a realistic estimate for your specific service and target area rather than a generic figure.',
      },
      {
        question: 'Do you have an office in Toronto?',
        answer:
          'No. WeBuildPro Global is remote-first and works with Toronto businesses without a local office. That is stated plainly rather than implied otherwise — what matters is whether the work produces customers, and everything is handled over calls, email and shared account access.',
      },
      {
        question: 'Can a new business compete in Toronto search results?',
        answer:
          'Not on head terms, at first. A new domain competing against businesses with years of history and established link profiles will lose those specifically. The winnable ground is long-tail: specific services, specific neighbourhoods, specific customer problems — where competition is a fraction as fierce.',
      },
      {
        question: 'Is SEO or Google Ads better for a Toronto service business?',
        answer:
          'Ads produce leads immediately and stop when spend stops. SEO takes months to build but compounds. In a market as competitive as Toronto, most businesses need ads for near-term cash flow while SEO develops, rather than choosing one.',
      },
    ],
    priorityServices: ['google-ads-management', 'seo', 'conversion-rate-optimization'],
  },
  {
    slug: 'mississauga',
    city: 'Mississauga',
    region: 'Ontario',
    regionCode: 'ON',
    country: 'Canada',
    metaTitle: 'Mississauga Digital Marketing Agency',
    metaDescription:
      'Websites, local SEO and Google Ads for Mississauga service businesses — lower click costs than Toronto, and a local-search advantage worth claiming.',
    h1: 'Digital Marketing for Mississauga Service Businesses',
    directAnswer:
      'Mississauga service businesses compete in a market with meaningfully lower advertising costs than neighbouring Toronto, while sharing the same customer base. WeBuildPro Global builds websites, local SEO and Google Ads campaigns for Mississauga businesses, prioritising the local-search visibility that converts best in suburban service markets.',
    cardSummary:
      'Lower click costs than Toronto, with local search still winnable.',
    keywords: [
      'digital marketing mississauga',
      'google ads management mississauga',
      'seo agency mississauga',
      'web design mississauga',
      'local seo mississauga',
      'marketing for mississauga contractors',
    ],
    marketContext: [
      'Mississauga occupies a useful position: large enough to generate substantial search volume, but without the bidding intensity that makes downtown Toronto keywords so expensive. The same job often costs noticeably less to acquire here than a few kilometres east.',
      'Local search carries more weight in Mississauga than in the Toronto core. Customers searching for a service here frequently add the city or their neighbourhood, and the map pack handles a large share of that traffic — which makes Google Business Profile optimisation unusually valuable relative to its cost.',
      'The city also has a dense small-business layer that has not fully professionalised its online presence. Many established local operators rank on reputation and referral with websites that convert poorly, which leaves room for a competitor who gets the basics right.',
    ],
    whatWorksHere: [
      {
        heading: 'Google Business Profile is the highest-return starting point',
        body: 'In suburban markets a large share of service searches resolve in the map pack rather than the organic results below it. A complete, verified, actively maintained Google Business Profile with real photos and steadily accumulating reviews frequently outperforms significant spend elsewhere — and costs nothing but attention.',
      },
      {
        heading: 'City-and-neighbourhood search terms are still available',
        body: 'Unlike Toronto, where most obvious local phrases are heavily contested, Mississauga and its neighbourhoods — Port Credit, Streetsville, Meadowvale, Erin Mills — still have searches without entrenched competition. These are low volume individually and high intent, which suits a limited budget well.',
      },
      {
        heading: 'Service-area clarity drives conversion',
        body: 'Customers in the western GTA routinely cannot tell from a website whether a business actually covers them, and ambiguity loses the enquiry. Stating the service area plainly and prominently is a small change that measurably improves conversion in this market.',
      },
    ],
    commonIndustries: [
      'Home services — plumbing, HVAC, electrical, drain',
      'Landscaping, snow removal and property maintenance',
      'Auto repair and mobile mechanics',
      'Cleaning services, residential and commercial',
      'Trades serving the industrial and warehouse corridor',
      'Health, dental and personal care practices',
    ],
    faqs: [
      {
        question: 'Is it cheaper to advertise in Mississauga than Toronto?',
        answer:
          'Generally yes. Cost per click in Mississauga typically runs below equivalent downtown Toronto keywords in the same category, because fewer advertisers compete for them. For a business that can serve both, deliberately separating the two geographically often lowers blended cost per lead.',
      },
      {
        question: 'How important is Google Business Profile for a Mississauga business?',
        answer:
          'Very. A large share of local service searches resolve in the map pack, and appearing there depends heavily on a complete, verified and actively maintained profile. For most local service businesses it is the highest-return item available and should be handled before paid spend scales.',
      },
      {
        question: 'Should I target Mississauga separately from Toronto?',
        answer:
          'If you serve both, yes. Separate campaigns let you bid differently where click costs differ sharply, write ads that reference the right area, and see cleanly which market produces better leads. A single GTA-wide campaign hides all of that.',
      },
      {
        question: 'Do you work with businesses across the wider GTA?',
        answer:
          'Yes — Mississauga, Brampton, Toronto, Oakville, Vaughan and the surrounding region. The agency is remote-first, so coverage is not limited by travel, and campaigns are built around whatever area your business can profitably serve.',
      },
    ],
    priorityServices: ['seo', 'google-ads-management', 'web-design-development'],
  },
  {
    slug: 'brampton',
    city: 'Brampton',
    region: 'Ontario',
    regionCode: 'ON',
    country: 'Canada',
    metaTitle: 'Brampton Digital Marketing Agency',
    metaDescription:
      'Websites, local SEO and Google Ads for Brampton service businesses — one of Canada’s fastest-growing cities, with local search still open.',
    h1: 'Digital Marketing for Brampton Service Businesses',
    directAnswer:
      'Brampton is one of Canada’s fastest-growing cities, which means steadily rising demand for home and trade services alongside competition that has not yet caught up online. WeBuildPro Global builds websites, local SEO and Google Ads campaigns for Brampton service businesses looking to claim that ground early.',
    cardSummary:
      'Fast-growing demand, and local search competition that has not caught up.',
    keywords: [
      'digital marketing brampton',
      'google ads management brampton',
      'seo agency brampton',
      'web design brampton',
      'local seo brampton',
      'marketing for brampton contractors',
    ],
    marketContext: [
      'Brampton has grown rapidly, and residential growth of that kind produces sustained demand for exactly the services local trades provide — new households needing plumbers, electricians, HVAC work, landscaping and renovation.',
      'Online competition has not kept pace with that growth. A substantial share of established Brampton service businesses operate with minimal web presence, relying on word of mouth and community networks. That leaves local search positions genuinely contestable in categories that would be locked up in a more mature market.',
      'The city is also highly multicultural, and search behaviour reflects it. Customers search in different languages and phrase the same need differently, which means keyword research based only on the most obvious English phrasing misses real volume.',
    ],
    whatWorksHere: [
      {
        heading: 'Early movers can still take local positions',
        body: 'In markets where most competitors have thin or outdated websites, getting the fundamentals right — a fast site, clear service pages, a verified Google Business Profile, accumulating reviews — is often enough to reach the top of local results. That window does not stay open indefinitely, but in several Brampton service categories it is still open.',
      },
      {
        heading: 'Keyword research should account for how people actually search',
        body: 'Brampton’s diversity means the same service is searched for in several phrasings, and assuming one standard wording leaves volume on the table. Research built on actual search data rather than assumption consistently surfaces terms competitors are not bidding on.',
      },
      {
        heading: 'Reviews carry disproportionate weight',
        body: 'In a market where buying decisions lean heavily on community reputation, visible review volume functions as a direct conversion lever as well as a ranking signal. A systematic process for requesting reviews after completed work is one of the cheapest competitive advantages available here.',
      },
    ],
    commonIndustries: [
      'Home services — plumbing, HVAC, electrical',
      'Renovation, construction and general contracting',
      'Landscaping, lawn care and snow removal',
      'Trucking, logistics and fleet services',
      'Auto repair and mobile mechanics',
      'Cleaning and property maintenance',
    ],
    faqs: [
      {
        question: 'Is Brampton a good market for local SEO?',
        answer:
          'Unusually good right now. The city has grown quickly while a large share of local service businesses maintain minimal web presence, so local search positions remain contestable in categories that would be locked up in a more established market. That advantage favours whoever moves first.',
      },
      {
        question: 'How long does local SEO take to work in Brampton?',
        answer:
          'On an established domain with a verified Google Business Profile, local movement often appears within two to four months. On a brand-new domain it takes longer, because search engines need time to build confidence in the site regardless of how well it is optimised.',
      },
      {
        question: 'Should a Brampton business advertise in Toronto too?',
        answer:
          'Only if the travel is worth it. Toronto clicks cost substantially more, so bidding there means paying premium rates for jobs that may not be profitable after travel time. Many Brampton businesses do better concentrating budget locally and expanding once local demand is captured.',
      },
      {
        question: 'What matters most for a Brampton service business online?',
        answer:
          'For most, a verified and active Google Business Profile with real reviews, followed by a fast website that states the service area clearly and makes calling easy. Paid ads work well on top of that foundation, and poorly without it.',
      },
    ],
    priorityServices: ['seo', 'web-design-development', 'google-ads-management'],
  },
  {
    slug: 'winnipeg',
    city: 'Winnipeg',
    region: 'Manitoba',
    regionCode: 'MB',
    country: 'Canada',
    metaTitle: 'Winnipeg Digital Marketing Agency',
    metaDescription:
      'Google Ads, SEO and websites for Winnipeg service businesses — a market where emergency-service search spikes hard with the weather.',
    h1: 'Digital Marketing for Winnipeg Service Businesses',
    directAnswer:
      'Winnipeg service businesses operate in a market defined by weather. Emergency plumbing, drain and heating searches spike sharply during freeze and thaw cycles, and campaigns that ignore that seasonality either miss the peak or overspend through the quiet months. WeBuildPro Global builds and manages campaigns around that pattern.',
    cardSummary:
      'A market where weather drives demand — and campaign timing decides results.',
    keywords: [
      'digital marketing winnipeg',
      'google ads management winnipeg',
      'seo agency winnipeg',
      'web design winnipeg',
      'emergency plumbing ads winnipeg',
      'marketing for winnipeg contractors',
    ],
    marketContext: [
      'Winnipeg’s climate produces demand patterns that few other Canadian markets share. Deep freezes cause frozen and burst pipes; spring thaw drives drain backups and basement flooding. Emergency search volume in these categories does not rise gradually — it spikes within days of a weather event.',
      'That pattern rewards campaigns built to respond. A flat monthly budget spread evenly across the year underspends precisely when demand peaks and overspends when nobody is searching. The accounts that perform best here treat budget as something to concentrate rather than distribute.',
      'Emergency intent also changes what the website must do. Someone with water on the basement floor is not reading an About page — they are looking for a phone number that works on the first tap. Page speed and immediate click-to-call matter more in this market than in most.',
    ],
    whatWorksHere: [
      {
        heading: 'Budget concentrated around weather events',
        body: 'Rather than spreading spend evenly, budget is weighted toward the periods when emergency search volume actually spikes. A cold snap or a thaw can produce more qualified searches in a week than the preceding month, and an account that cannot flex to meet that demand leaves the best leads to whoever can.',
      },
      {
        heading: 'Emergency keywords demand an emergency-ready page',
        body: 'Urgent searches convert on speed and clarity. A fast page, a phone number visible without scrolling, click-to-call that works on the first tap, and an explicit statement that the business answers after hours. Anything that adds a step between the search and the call loses the job to a competitor whose page does not.',
      },
      {
        heading: 'Conversion tracking has to count calls',
        body: 'In emergency categories most leads arrive by phone, not form. An account tracking only form submissions is blind to the majority of its conversions and will optimise toward the wrong keywords. Call tracking is not optional in this market — it is the only way the data reflects reality.',
      },
    ],
    commonIndustries: [
      'Emergency plumbing and drain services',
      'HVAC, furnace and heating repair',
      'Water damage restoration and basement waterproofing',
      'Roofing and ice-dam work',
      'Snow removal and seasonal property services',
      'Auto repair and towing',
    ],
    faqs: [
      {
        question: 'When is the busiest season for Winnipeg home services?',
        answer:
          'It depends on the trade, but weather drives most of it. Deep-freeze periods spike frozen and burst pipe calls; spring thaw drives drain backups and basement flooding. Campaigns built around those windows generally outperform campaigns that spend evenly across the year.',
      },
      {
        question: 'Do emergency service ads work in Winnipeg?',
        answer:
          'They work well when the landing page matches the urgency. Emergency searchers convert on speed — a fast page, a visible phone number and working click-to-call. The ads themselves are rarely the limiting factor; the page they land on usually is.',
      },
      {
        question: 'Why does call tracking matter so much here?',
        answer:
          'Because in emergency categories most leads call rather than fill in a form. An account measuring only form submissions misses the majority of its conversions, which means its optimisation decisions are based on a small and unrepresentative slice of what actually happened.',
      },
      {
        question: 'Is Winnipeg less competitive than Toronto for paid search?',
        answer:
          'Generally yes — fewer advertisers compete, so cost per click tends to be lower. The trade-off is lower search volume overall, which makes it more important that each click lands on a page built to convert rather than one that leaks enquiries.',
      },
    ],
    priorityServices: ['google-ads-management', 'web-design-development', 'conversion-rate-optimization'],
  },
];

export function getLocation(slug: string): LocationDefinition | undefined {
  return LOCATIONS.find((location) => location.slug === slug);
}
